/* eslint-disable jsx-a11y/anchor-is-valid */
import { useNavigate } from "react-router-dom";
import { useSidebar } from "../context/SidebarContext";
import { CallIcon, LogoutIcon } from "../icons";
import { useEffect, useState } from "react";
import logoSidebar from "../assets/logo/logo_sidebar.png";
import { useNavigation } from "../context/NavigationContext";
import RegisterModal from "../pages/Auth/Register/home";
import LoginModal from "../pages/Auth/Login/home";
import logo from "../assets/logo/logo.png";
import { MinusIcon, PlusIcon } from "lucide-react";
import httpService from "../services/httpService";
import avatar from "../assets/images/user/avatar.png";
import { useAuth } from "../context/AuthContext";

// Dropdown structure (same as header)
const dropdownItems = {
    Company: [
        "Team & Conditions",
        "Privacy Policy",
        "Warranty Policy",
        "Warranty Claim",
        "Meet Our Team",
        "Contact Us",
        "FAQs",
        "Disclaimer",
    ],
    Live: ["Live", "Latest News"],
};

// Menu structure (same as header)
// const allMenuItems = [
//     "Home",
//     "Franchise",
//     "Company",
//     // "Jobs",
//     ...(user ? ["Dashboard"] : ["Jobs"]),
//     "About Us",
//     "Offers Today",
// ];


// Main routes (same as AppHeader)
const menuRoutes: Record<string, string> = {
    Home: "/",
    Franchise: "/franchise",
    Company: "/company",
    Jobs: "/jobs",
    Dashboard: "/applicant/dashboard",
    "About Us": "/about",
    "Offers Today": "/offers",
};

// Dropdown routes (same as AppHeader)
const dropdownRoutes: Record<string, string> = {
    "Team & Conditions": "/company/terms",
    "Privacy Policy": "/company/privacy-policy",
    "Warranty Policy": "/company/warranty-policy",
    "Warranty Claim": "/company/warranty-claim",
    "Meet Our Team": "/company/team",
    "Contact Us": "/company/contact",
    FAQs: "/company/faqs",
    Disclaimer: "/company/disclaimer",
};

const AppSidebar: React.FC = () => {
    const { isMobileOpen, toggleMobileSidebar } = useSidebar();
    const { activeNav, setActiveNav } = useNavigation();
    const [openDropdown, setOpenDropdown] = useState<string | null>(null);
    const [isRegisterOpen, setRegisterOpen] = useState(false);
    const [isLoginOpen, setLoginOpen] = useState(false);

    const navigate = useNavigate();
    const { user, fetchUser, logout } = useAuth();

    const allMenuItems = [
        "Home",
        "Franchise",
        "Company",
        // "Jobs",
        ...(user ? ["Dashboard"] : ["Jobs"]),
        "About Us",
        "Offers Today",
    ];

    useEffect(() => {
        document.body.style.overflow = isMobileOpen ? "hidden" : "";
        return () => {
            document.body.style.overflow = "";
        };
    }, [isMobileOpen]);

    useEffect(() => {
        const parent = Object.keys(dropdownItems).find((key) =>
            dropdownItems[key as keyof typeof dropdownItems].includes(activeNav)
        );
        if (parent) {
            setOpenDropdown(parent);
        }
    }, [activeNav]);

    useEffect(() => {
        if (isMobileOpen) {
            fetchUser();
        }
    }, [isMobileOpen]);

    const toggleDropdown = (title: string) => {
        if (openDropdown === title) {
            setOpenDropdown(null);
        } else {
            setOpenDropdown(title);
            setActiveNav("");
        }
    };


    const handleNavigation = (item: string) => {
        setActiveNav(item);
        const route = menuRoutes[item];
        if (route) {
            navigate(route);
            window.scrollTo({ top: 0, behavior: "smooth" });
            if (isMobileOpen) toggleMobileSidebar();
        }
    };

    const handleSubMenuClick = (subItem: string) => {
        setActiveNav(subItem);
        const parent = Object.keys(dropdownItems).find(key =>
            dropdownItems[key as keyof typeof dropdownItems].includes(subItem)
        );
        if (parent) setOpenDropdown(parent);
        const route = dropdownRoutes[subItem];
        if (route) {
            navigate(route);
            window.scrollTo({ top: 0, behavior: "smooth" });

            setTimeout(() => {
                if (isMobileOpen) toggleMobileSidebar();
            }, 150);
        }

    };

    return (
        <>
            <RegisterModal
                isOpen={isRegisterOpen}
                onClose={() => setRegisterOpen(false)}
            />

            <LoginModal isOpen={isLoginOpen}
                onClose={() => setLoginOpen(false)}
                onSuccess={() => {
                    fetchUser();
                    setLoginOpen(false);
                }} />

            {/* Overlay */}
            <div
                className={`fixed inset-0 bg-black/30 transition-opacity duration-300 z-[999] ${isMobileOpen ? "opacity-100 pointer-events-auto" : "opacity-0 pointer-events-none"
                    }`}
                onClick={toggleMobileSidebar}
            />

            {/* Sidebar Drawer */}
            <aside
                className={`
                    fixed top-0 left-0 z-[1000] h-full bg-white
                    w-full max-w-md sm:max-w-lg
                    overflow-y-auto
                    transform transition-transform duration-[400ms] ease-out
                    will-change-transform translate-z-0
                    ${isMobileOpen ? "translate-x-0" : "-translate-x-full"}
                `}
            >


                {/* Sidebar Header */}
                <div className="flex items-center justify-between px-4 py-3">
                    <img src={logo} alt="PhoneLab Logo" className="w-28 object-contain" />
                    <button
                        className="p-1 text-base text-neutral-500 rounded"
                        onClick={toggleMobileSidebar}
                    >
                        ✕
                    </button>
                </div>

                {/* CTA Buttons */}
                <div className="mt-6 flex items-center gap-4 px-14">
                    {!user ? (
                        <div className="flex gap-4 w-full">
                            <button
                                onClick={() => { setLoginOpen(true); toggleMobileSidebar() }}
                                className="w-full bg-white brand-text border rounded-lg brand-border py-2 text-sm transition duration-150"
                            >
                                Login
                            </button>

                            <button
                                onClick={() => { setRegisterOpen(true); toggleMobileSidebar() }}
                                className="w-full brand-bg brand-bg-hover text-white py-2 text-sm rounded-lg transition duration-150"
                            >
                                Register
                            </button>
                        </div>
                    ) : (
                        <div
                            className="flex flex-col items-center justify-center gap-4 w-full"
                        >
                            <div className="size-20 rounded-full brand-bg text-white flex items-center justify-center text-sm font-bold overflow-hidden">
                                <img
                                    src={user?.profileImage || avatar}
                                    alt="avatar"
                                    className="w-full h-full object-cover"
                                    onError={(e) => {
                                        e.currentTarget.src = avatar;
                                    }}
                                />
                            </div>

                            <div className="flex flex-col items-center justify-center">
                                <span className="text-sm font-medium text-gray-700 max-w-[120px] truncate">
                                    {user?.fullName}
                                </span>
                                <span className="text-xs font-medium text-gray-400">
                                    Applicant
                                </span>
                            </div>

                            <button
                                onClick={() => {
                                    logout();
                                    navigate("/");
                                }}
                                className="border border-red-400 rounded-md w-full text-red-500 py-1 flex items-center gap-4 justify-center">
                                <LogoutIcon />
                                Logout
                            </button>
                        </div>
                    )}
                </div>

                {/* Sidebar Navigation */}
                <nav className="px-5 py-6">
                    <ul className="flex flex-col gap-y-2">
                        {allMenuItems.map((item) => {
                            const isDropdown = Object.keys(dropdownItems).includes(item);
                            const isOpen = openDropdown === item;

                            return (
                                <li key={item}>
                                    {isDropdown ? (
                                        <>
                                            <button
                                                onClick={() => toggleDropdown(item)}
                                                className={`w-full py-2 px-3 font-roboto flex items-center justify-between text-sm border-t pt-4 border-gray-300 transition 
                                                    ${(openDropdown === item ||
                                                        dropdownItems[item as keyof typeof dropdownItems].includes(activeNav))
                                                        ? "brand-bg text-white"
                                                        : "text-zinc-900"
                                                    }`}
                                            >
                                                <span className="font-light">{item}</span>

                                                <PlusIcon
                                                    className={`size-4 text-gray-700 ${isOpen ? "hidden" : "block"}`}
                                                />

                                                <MinusIcon
                                                    className={`size-4 text-white ${isOpen ? "block" : "hidden"}`}
                                                />
                                            </button>
                                            {isOpen && (
                                                <ul className="px-4 py-1 animate-slideDown">
                                                    {dropdownItems[item as keyof typeof dropdownItems].map((subItem) => (
                                                        <li key={subItem}>
                                                            <button
                                                                onClick={() => handleSubMenuClick(subItem)}
                                                                className={`block border-t border-gray-300 w-full text-left px-3 py-2 text-sm rounded-sm transition ${activeNav === subItem
                                                                    ? "brand-bg text-white font-medium"
                                                                    : "text-black"
                                                                    }`}
                                                            >
                                                                {subItem}
                                                            </button>
                                                        </li>
                                                    ))}
                                                </ul>
                                            )}
                                        </>
                                    ) : (
                                        <button
                                            onClick={() => handleNavigation(item)}
                                            className={`w-full text-left font-roboto px-3 py-2 pt-4 text-sm border-t border-gray-300 transition ${activeNav === item
                                                ? "brand-bg text-white"
                                                : "text-zinc-900"
                                                }`}
                                        >
                                            {item}


                                        </button>
                                    )}

                                </li>
                            );
                        })}
                    </ul>

                    {/* Footer Social Section */}
                    <div className="mt-8 text-center px-2">
                        {/* <p className="text-sm font-rubik">
                            *Earn Cashback on Every Repair! 🎉 Up to ₹200 cashback with every repair invoice.
                        </p> */}
                        <p className="text-sm font-rubik underline">
                            Our Social Link
                        </p>
                        <div className="mt-4 flex items-center justify-center gap-4">
                            <a href="#" className="w-9 h-9 bg-zinc-200 rounded-full brand-text brand-hover-bg flex items-center justify-center transition">
                                <svg aria-hidden="true" fill="currentColor" className="w-5 h-5 e-font-icon-svg e-fab-facebook" viewBox="0 0 512 512" xmlns="http://www.w3.org/2000/svg">
                                    <path d="M504 256C504 119 393 8 256 8S8 119 8 256c0 123.78 90.69 226.38 209.25 245V327.69h-63V256h63v-54.64c0-62.15 37-96.48 93.67-96.48 27.14 0 55.52 4.84 55.52 4.84v61h-31.28c-30.8 0-40.41 19.12-40.41 38.73V256h68.78l-11 71.69h-57.78V501C413.31 482.38 504 379.78 504 256z"></path></svg>
                            </a>
                            <a href="#" className="w-9 h-9 p-1 bg-zinc-200 rounded-full brand-text brand-hover-bg flex items-center justify-center transition">
                                <svg aria-hidden="true" fill="currentColor" className="w-5 h-5 e-font-icon-svg e-fab-twitter" viewBox="0 0 512 512" xmlns="http://www.w3.org/2000/svg">
                                    <path d="M459.37 151.716c.325 4.548.325 9.097.325 13.645 0 138.72-105.583 298.558-298.558 298.558-59.452 0-114.68-17.219-161.137-47.106 8.447.974 16.568 1.299 25.34 1.299 49.055 0 94.213-16.568 130.274-44.832-46.132-.975-84.792-31.188-98.112-72.772 6.498.974 12.995 1.624 19.818 1.624 9.421 0 18.843-1.3 27.614-3.573-48.081-9.747-84.143-51.98-84.143-102.985v-1.299c13.969 7.797 30.214 12.67 47.431 13.319-28.264-18.843-46.781-51.005-46.781-87.391 0-19.492 5.197-37.36 14.294-52.954 51.655 63.675 129.3 105.258 216.365 109.807-1.624-7.797-2.599-15.918-2.599-24.04 0-57.828 46.782-104.934 104.934-104.934 30.213 0 57.502 12.67 76.67 33.137 23.715-4.548 46.456-13.32 66.599-25.34-7.798 24.366-24.366 44.833-46.132 57.827 21.117-2.273 41.584-8.122 60.426-16.243-14.292 20.791-32.161 39.308-52.628 54.253z"></path></svg>
                            </a>
                            <a href="#" className="w-9 h-9 p-1 bg-zinc-200 rounded-full brand-text brand-hover-bg flex items-center justify-center transition">
                                <svg aria-hidden="true" fill="currentColor" className="w-5 h-5 e-font-icon-svg e-fab-youtube" viewBox="0 0 576 512" xmlns="http://www.w3.org/2000/svg">
                                    <path d="M549.655 124.083c-6.281-23.65-24.787-42.276-48.284-48.597C458.781 64 288 64 288 64S117.22 64 74.629 75.486c-23.497 6.322-42.003 24.947-48.284 48.597-11.412 42.867-11.412 132.305-11.412 132.305s0 89.438 11.412 132.305c6.281 23.65 24.787 41.5 48.284 47.821C117.22 448 288 448 288 448s170.78 0 213.371-11.486c23.497-6.321 42.003-24.171 48.284-47.821 11.412-42.867 11.412-132.305 11.412-132.305s0-89.438-11.412-132.305zm-317.51 213.508V175.185l142.739 81.205-142.739 81.201z"></path></svg>
                            </a>
                        </div>

                        {/* <div className="mt-8">
                            <a
                                href="tel:+918695864141"
                                className="inline-flex items-center justify-center gap-1 text-xl font-bold text-gray-800 brand-hover-text transition"
                            >
                                <div className="w-7 h-7 p-1 brand-bg rounded-full flex items-center justify-center">
                                    <CallIcon className="w-4 fill-white" />
                                </div>
                                <span className="text-lg ">+91 8695 86 4141</span>
                            </a>
                        </div> */}
                    </div>
                </nav>
            </aside>
        </>
    );
};

export default AppSidebar;
